import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
console.info('[BOOT] Starting application bootstrap... main.ts loaded');
function hasUsableValues(config: Record<string, string | undefined>): boolean {
  return Object.values(config).some((value) => typeof value === 'string' && value.trim().length > 0);
}

// Dynamically injects env.js as a script element and resolves when done.
// Errors (including SyntaxError from SPA HTML rewrites) are caught so they
// never kill the page — env.js is a local-dev fallback only.
function tryInjectEnvJs(): Promise<void> {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = '/env.js';
    script.onload = () => {
      console.info('[BOOT] env.js loaded as local-dev fallback');
      resolve();
    };
    script.onerror = () => {
      console.warn('[BOOT] env.js not available — expected in production, config.json is the runtime source');
      resolve();
    };
    document.head.appendChild(script);
  });
}

async function loadRuntimeEnv(): Promise<void> {
  const runtimeWindow = window as unknown as {
    __env__?: Record<string, string | undefined>;
  };

  // config.json is written into the zip by the pipeline at deploy time.
  // env.js is a local-dev fallback injected dynamically below.
  const configCandidates = ['/config.json', '/assets/config.json', '/browser/assets/config.json'];

  for (const configPath of configCandidates) {
    try {
      console.info(`[BOOT] Trying runtime configuration from ${configPath}`);
      const response = await fetch(configPath, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        console.warn(`[BOOT] Runtime config not available at ${configPath}: HTTP ${response.status}`);
        continue;
      }
      console.info(`[BOOT] Loaded runtime configuration from ${configPath}`);
      

      // A SPA rewrite rule answers unknown paths with index.html, so verify we really got JSON.
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('json')) {
        console.warn(`[BOOT] Runtime config candidate ${configPath} returned non-JSON content-type: ${contentType}`);
        continue;
      }

      const json = (await response.json()) as Record<string, string | undefined>;
      if (json && typeof json === 'object' && hasUsableValues(json)) {
        runtimeWindow.__env__ = { ...(runtimeWindow.__env__ ?? {}), ...json };
        console.info(`[BOOT] Runtime configuration merged from ${configPath}`);
        return;
      }
      console.warn(`[BOOT] Runtime config at ${configPath} did not contain usable values`);
    } catch (error) {
      console.warn(`[BOOT] Failed reading runtime config from ${configPath}`, error);
    }
  }

  // config.json not found — try env.js (local dev only).
  // Loaded dynamically so a SyntaxError (Azure returning index.html for /env.js)
  // is caught by onerror and never halts page execution.
  await tryInjectEnvJs();

  if (!runtimeWindow.__env__ || !hasUsableValues(runtimeWindow.__env__)) {
    console.error('[BOOT] No runtime configuration found. Checked config.json and env.js.');
  }
}

loadRuntimeEnv()
  .then(() => {
    console.info('[BOOT] Bootstrapping Angular application');
    return bootstrapApplication(AppComponent, appConfig);
  })
  .catch((err) => console.error(err));
 